import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReports } from '../../hooks/useMarketing';

export const REPORT_STATUS_STYLES = {
  generating: { background: '#eff6ff', color: '#1d4ed8', label: 'Generating' },
  draft: { background: '#fffbeb', color: '#b45309', label: 'Draft — review & send' },
  sent: { background: '#ecfdf5', color: '#047857', label: 'Sent' },
  failed: { background: '#fef2f2', color: '#dc2626', label: 'Failed' },
};

const SIGNAL_LABELS = {
  not_found: { label: 'Register clear', color: '#047857' },
  our_filing_visible: { label: 'Own filing visible', color: '#0f766e' },
  third_party_match: { label: 'Third-party match', color: '#dc2626' },
  unavailable: { label: 'Search unavailable', color: '#b45309' },
};

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'generating', label: 'Generating' },
  { key: 'failed', label: 'Failed' },
  { key: 'sent', label: 'Sent' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * Reports — the 24-hour SLA queue for the quote-first funnel.
 * Draft reports need a human review + send; failed ones need a regenerate.
 */
function Reports() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const { data, isLoading, error } = useReports(statusFilter ? { status: statusFilter } : {});

  const reports = data?.reports || [];

  return (
    <div className="mkt-page">
      <div className="mkt-page-header">
        <div>
          <h1>Reports</h1>
          <p className="mkt-page-subtitle">
            Free report queue — visitors are promised their report and confirmed quote within 24 hours of requesting it.
          </p>
        </div>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`mkt-filter-pill${statusFilter === f.key ? ' active' : ''}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="mkt-loading"><div className="mkt-loading-spinner" /><p>Loading reports...</p></div>
      )}
      {error && <p style={{ color: '#dc2626' }}>Failed to load reports: {error.message}</p>}

      {!isLoading && reports.length === 0 && (
        <p style={{ color: '#64748b' }}>No reports{statusFilter ? ` with status "${statusFilter}"` : ''} yet.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {reports.map((r) => {
          const { label: statusLabel, ...statusStyle } = REPORT_STATUS_STYLES[r.status] || {};
          const signal = r.registerSignal ? SIGNAL_LABELS[r.registerSignal] : null;
          const waitingSince = r.requestedAt || r.generatedAt;
          return (
            <div
              key={r.id}
              className="mkt-card"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/marketing/reports/${r.id}`)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span className="mkt-card-name">
                  {r.trademarkName ? `"${r.trademarkName}"` : r.applicationId}
                </span>
                <span style={{ ...statusStyle, borderRadius: '4px', fontSize: '11px', fontWeight: 700, padding: '2px 8px' }}>
                  {statusLabel || r.status}
                </span>
                {signal && (
                  <span style={{ background: `${signal.color}12`, color: signal.color, borderRadius: '4px', fontSize: '11px', fontWeight: 600, padding: '2px 8px' }}>
                    {signal.label}
                  </span>
                )}
                {/* A hand-set price differs from the price list on purpose, so
                    it reads as a custom price; only an unexplained difference
                    is worth flagging in red */}
                {r.quoteMismatch && (r.quoteManual ? (
                  <span
                    style={{ background: '#f1f5f9', color: '#475569', borderRadius: '4px', fontSize: '11px', fontWeight: 600, padding: '2px 8px' }}
                    title={r.quoteExpectedTotal != null ? `Priced by hand. Standard price would be ${r.quoteCurrency || 'AUD'} $${Number(r.quoteExpectedTotal).toLocaleString()}` : 'Priced by hand'}
                  >
                    Custom price
                  </span>
                ) : (
                  <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: '4px', fontSize: '11px', fontWeight: 700, padding: '2px 8px' }} title="Stored quote total does not match the server recompute">
                    Quote mismatch
                  </span>
                ))}
                {r.quoteRevisionPending && (
                  <span style={{ background: '#fffbeb', color: '#b45309', borderRadius: '4px', fontSize: '11px', fontWeight: 700, padding: '2px 8px' }} title="A revised quote has been saved but not sent to the client">
                    Revision {r.quoteRevision} unsent
                  </span>
                )}
                {r.provisioningError && (
                  <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: '4px', fontSize: '11px', fontWeight: 700, padding: '2px 8px' }} title={r.provisioningError}>
                    Provisioning failed
                  </span>
                )}
                <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '12px' }}>
                  {r.status === 'sent'
                    ? `Sent ${timeAgo(r.sentAt)}`
                    : waitingSince ? `Requested ${timeAgo(waitingSince)}` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '14px', marginTop: '6px', color: '#64748b', fontSize: '13px', flexWrap: 'wrap' }}>
                {r.contact?.name && <span>{r.contact.name}</span>}
                {r.contact?.email && <span>{r.contact.email}</span>}
                {r.businessName && <span>{r.businessName}</span>}
                {r.quoteTotal != null && (
                  <span style={{ fontWeight: 600, color: '#0f766e' }}>
                    {r.quoteCurrency || ''} ${Number(r.quoteTotal).toLocaleString()}
                  </span>
                )}
              </div>
              {r.error && (
                <div style={{ marginTop: '6px', color: '#dc2626', fontSize: '12px' }}>{r.error}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Reports;
